import React, { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { 
  Sparkles, 
  Crown, 
  Star, 
  Heart,
  ArrowRight,
  MapPin,
  Users,
  Clock,
  Calendar,
  ChefHat,
  Music,
  Palette,
  Waves
} from 'lucide-react';

const ExperiencesPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'Toutes', icon: Sparkles },
    { id: 'culinary', name: 'Gastronomie', icon: ChefHat },
    { id: 'cultural', name: 'Culture & Art', icon: Palette },
    { id: 'adventure', name: 'Aventure', icon: Waves },
    { id: 'wellness', name: 'Bien-être', icon: Heart },
    { id: 'entertainment', name: 'Divertissement', icon: Music }
  ];

  const experiences = [
    {
      id: 1,
      category: 'culinary',
      title: "Dîner Privé avec Chef Étoilé",
      subtitle: "Menu dégustation exclusif dans votre villa",
      image: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      price: "À partir de 2 500€",
      duration: "4 heures",
      guests: "Jusqu'à 12 personnes",
      location: "Votre villa",
      highlights: ["Chef Michelin 2 étoiles", "Produits ultra-premium", "Service majordome", "Accord mets-vins"],
      description: "Un chef étoilé se déplace dans votre villa pour créer un menu gastronomique sur-mesure avec des produits d'exception."
    },
    {
      id: 2,
      category: 'cultural',
      title: "Visite Privée du Louvre Fermé",
      subtitle: "Accès exclusif aux œuvres après fermeture",
      image: "https://images.unsplash.com/photo-1541961017774-22349e4a1262?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      price: "À partir de 5 000€",
      duration: "3 heures",
      guests: "Jusqu'à 8 personnes",
      location: "Paris, France",
      highlights: ["Musée fermé au public", "Guide conférencier expert", "Œuvres interdites", "Transport VIP"],
      description: "Découvrez le Louvre comme jamais, dans le silence et l'intimité absolue, avec un accès à des œuvres habituellement non exposées."
    },
    {
      id: 3,
      category: 'adventure',
      title: "Hélicoptère vers Île Privée",
      subtitle: "Pique-nique de luxe sur plage secrète",
      image: "https://images.unsplash.com/photo-1544016820-e7de5b4b0eb7?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      price: "À partir de 3 500€",
      duration: "6 heures",
      guests: "Jusqu'à 6 personnes",
      location: "Côte d'Azur",
      highlights: ["Hélicoptère privé", "Île déserte exclusive", "Chef sur place", "Retour au coucher du soleil"],
      description: "Évadez-vous vers une île privée accessible uniquement par hélicoptère pour un pique-nique gastronomique sur une plage paradisiaque."
    },
    {
      id: 4,
      category: 'wellness',
      title: "Retraite Spa Himalayenne",
      subtitle: "Maître spirituel et soins ancestraux",
      image: "https://images.unsplash.com/photo-1540555700478-4be289fbecef?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      price: "À partir de 1 800€",
      duration: "8 heures",
      guests: "Jusqu'à 4 personnes",
      location: "Votre villa",
      highlights: ["Maître tibétain authentique", "Méditation guidée", "Massages aux huiles rares", "Cérémonie du thé"],
      description: "Un maître spirituel tibétain se déplace avec son équipe pour une journée complète de purification et ressourcement."
    },
    {
      id: 5,
      category: 'entertainment',
      title: "Concert Privé Pianiste Virtuose",
      subtitle: "Récital exclusif au coucher de soleil",
      image: "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      price: "À partir de 4 200€",
      duration: "2 heures",
      guests: "Jusqu'à 20 personnes",
      location: "Terrasse de votre villa",
      highlights: ["Pianiste international", "Piano à queue Steinway", "Répertoire personnalisé", "Champagne millésimé"],
      description: "Un concert privé avec un pianiste de renommée mondiale dans le cadre enchanteur de votre terrasse face au coucher de soleil."
    },
    {
      id: 6,
      category: 'cultural',
      title: "Atelier Peinture avec Artiste Coté",
      subtitle: "Création d'œuvre originale assistée",
      image: "https://images.unsplash.com/photo-1460661419201-fd4cecdf8a04?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80",
      price: "À partir de 2 800€",
      duration: "5 heures",
      guests: "Jusqu'à 6 personnes",
      location: "Atelier d'artiste",
      highlights: ["Artiste côté internationale", "Œuvre personnalisée", "Techniques exclusives", "Toiles professionnelles"],
      description: "Créez votre propre œuvre d'art sous la guidance d'un artiste reconnu, dans son atelier privé avec vue panoramique."
    }
  ];

  const filteredExperiences = activeCategory === 'all' 
    ? experiences 
    : experiences.filter(exp => exp.category === activeCategory);

  const processSteps = [
    {
      step: "1",
      title: "Consultation",
      description: "Échange approfondi sur vos envies et contraintes"
    },
    {
      step: "2", 
      title: "Conception",
      description: "Création d'une expérience 100% personnalisée"
    },
    {
      step: "3",
      title: "Coordination",
      description: "Organisation complète par notre équipe dédiée"
    },
    {
      step: "4",
      title: "Création",
      description: "Réalisation de votre expérience impossible"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1414235077428-338989a2e8c0?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/70" />
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center text-white">
          <Badge 
            variant="secondary" 
            className="mb-6 px-6 py-3 bg-white/10 backdrop-blur-md border-white/20 text-white text-sm font-light uppercase tracking-wider"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Expériences Impossibles
          </Badge>
          
          <h1 
            className="text-5xl md:text-6xl font-light mb-6 tracking-wide"
            style={{ 
              fontFamily: 'Playfair Display, serif',
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)'
            }}
          >
            Expériences Sur-Mesure
          </h1>
          
          <p 
            className="text-xl md:text-2xl font-light mb-12 opacity-90 max-w-3xl mx-auto leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Nous créons l'impossible. Des expériences uniques au monde, conçues spécialement pour vous et réalisées par les meilleurs experts
          </p>

          <Button 
            size="lg"
            className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
          >
            Créer mon Expérience
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="py-12 px-6 bg-white/50 backdrop-blur-sm">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-wrap justify-center gap-4">
            {categories.map((category) => {
              const IconComponent = category.icon;
              return (
                <Button
                  key={category.id}
                  variant={activeCategory === category.id ? 'default' : 'outline'}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-6 py-3 rounded-full transition-all duration-300 ${
                    activeCategory === category.id
                      ? 'bg-gradient-to-r from-amber-600 to-amber-700 text-white shadow-lg transform -translate-y-1'
                      : 'bg-white/80 text-gray-700 border-gray-200 hover:bg-white/90 backdrop-blur-sm'
                  }`}
                >
                  <IconComponent className="w-4 h-4 mr-2" />
                  {category.name}
                </Button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Experiences Grid */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredExperiences.map((experience) => (
              <Card key={experience.id} className="overflow-hidden hover:shadow-2xl transition-all duration-500 group">
                <div className="relative h-64">
                  <img 
                    src={experience.image}
                    alt={experience.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  
                  <div className="absolute top-4 right-4">
                    <Badge className="bg-white/20 backdrop-blur-md border-white/30 text-white">
                      <Crown className="w-3 h-3 mr-1" />
                      Exclusif
                    </Badge>
                  </div>

                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <h3 
                      className="text-xl font-light mb-2"
                      style={{ fontFamily: 'Playfair Display, serif' }}
                    >
                      {experience.title}
                    </h3>
                    <p className="text-sm opacity-90">{experience.subtitle}</p>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-2xl font-light text-amber-600">
                      {experience.price}
                    </div>
                    <div className="flex gap-4 text-xs text-gray-500">
                      <div className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        <span>{experience.duration}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        <span>{experience.guests}</span>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-600 text-sm leading-relaxed mb-4">
                    {experience.description}
                  </p>

                  <div className="space-y-2 mb-6">
                    {experience.highlights.slice(0, 2).map((highlight, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <Star className="w-3 h-3 text-amber-500" />
                        <span className="text-xs text-gray-600">{highlight}</span>
                      </div>
                    ))}
                  </div>

                  <Button 
                    className="w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white transition-all duration-300"
                  >
                    Réserver cette Expérience
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 px-6 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl font-light mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              Comment nous Créons l'Impossible
            </h2>
            <p 
              className="text-xl opacity-90 max-w-2xl mx-auto leading-relaxed"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              De l'idée à la réalisation, notre processus garantit une expérience parfaite
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-amber-500 to-amber-600 rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-light text-white">{step.step}</span>
                </div>
                <h3 className="text-xl font-light mb-4">{step.title}</h3>
                <p className="text-gray-300 text-sm leading-relaxed">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 
            className="text-4xl font-light mb-6 text-gray-800"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Votre Rêve le Plus Fou ?
          </h2>
          <p 
            className="text-xl mb-8 text-gray-600 leading-relaxed max-w-2xl mx-auto"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Partagez-nous votre vision la plus ambitieuse. Notre équipe de créateurs d'expériences 
            trouve toujours un moyen de la réaliser.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300 transform hover:scale-105"
            >
              Créer une Expérience Unique
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-2 border-amber-600 text-amber-600 hover:bg-amber-600 hover:text-white px-8 py-4 rounded-full font-medium text-lg transition-all duration-300"
            >
              Parcourir le Catalogue
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-sm text-gray-600">
            <div className="flex items-center justify-center gap-2">
              <Calendar className="w-4 h-4 text-amber-600" />
              <span>Disponible toute l'année</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <MapPin className="w-4 h-4 text-amber-600" />
              <span>Partout dans le monde</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <Crown className="w-4 h-4 text-amber-600" />
              <span>100% sur-mesure</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ExperiencesPage;